import React from 'react';
import type {
    TopNavigationProps,
    ListItemProps,
    ShorthandValue,
    BoxProps,
} from '@archipro-design/aria';
import { Flex, pxToRem, Image, Caption } from '@archipro-design/aria';
import { ChevronRight } from '@archipro-design/icons';

import type { RenderAnchorTag } from '../types';

const MAJOR_NAV_ITEMS: { link: string; label: string }[] = [
    { label: 'Search Products', link: '/products' },
    { label: 'Browse Projects', link: '/projects' },
    { label: 'Connect with Professionals', link: '/professionals' },
    { label: 'Read Archizeen', link: '/articles' },
];

const MINOR_NAV_ITEMS: ListItemProps[] = [
    {
        label: 'Digital Magazines',
        link: '/digital-magazines',
        iconAfter: <ChevronRight variables={{ '1xSize': pxToRem(12) }} />,
    },
    {
        label: 'About us',
        link: '/about-us',

        iconAfter: <ChevronRight variables={{ '1xSize': pxToRem(12) }} />,
    },
    {
        label: 'Careers',
        link: 'https://apply.workable.com/archipro-3/',

        iconAfter: <ChevronRight variables={{ '1xSize': pxToRem(12) }} />,
    },
    {
        label: 'Contact',
        link: '/contact-us',
        iconAfter: <ChevronRight variables={{ '1xSize': pxToRem(12) }} />,
    },
];

const HDE_NAV_ITEM: ListItemProps = {
    label: 'Home Design Evening',
    link: '/home-design-event',
    iconAfter: <ChevronRight variables={{ '1xSize': pxToRem(12) }} />,
};

const EVENTS_ITEMS: { imageSrc: string; link: string; title: string }[] = [
    {
        imageSrc: '/assets/website/images/ui/events_mge_mobile.png',
        title: 'Home Design Evening',
        link: '/home-design-event',
    },
];

export interface UseAppMenuSlotOpts {
    renderAnchorTag: RenderAnchorTag;
    onCategoryItemClick: (link: string) => void;
    showSearchMenu?: boolean;
    assetsBaseUrl: string;
    bottomNavigationAdditionalContent?: ShorthandValue<BoxProps>;
    showHDENavLink?: boolean;
}

export function useAppMenuSlot({
    renderAnchorTag,
    onCategoryItemClick,
    showSearchMenu = false,
    assetsBaseUrl,
    bottomNavigationAdditionalContent,
    showHDENavLink = false,
}: UseAppMenuSlotOpts): TopNavigationProps['appMenu'] {
    const minorNavItems = showHDENavLink
        ? [
              ...MINOR_NAV_ITEMS.slice(0, 2),
              HDE_NAV_ITEM,
              ...MINOR_NAV_ITEMS.slice(2),
          ]
        : MINOR_NAV_ITEMS;

    return {
        majorNavItems: {
            items: MAJOR_NAV_ITEMS.map(({ label, link }) => ({
                label,
                link,
                key: link,
                iconAfter: (
                    <ChevronRight variables={{ '1xSize': pxToRem(12) }} />
                ),
            })),
            onItemClick: (_, data) => {
                if (!data?.link) return;
                onCategoryItemClick(data.link);
            },
        },
        minorNavItems: {
            items: minorNavItems.map(({ link, ...rest }) => ({
                ...rest,
                ...(link && renderAnchorTag(link)),
                key: link,
            })),
        },
        eventItems: showHDENavLink
            ? EVENTS_ITEMS.map((event) => ({
                  key: event.link,
                  children: (
                      <Flex column {...renderAnchorTag(event.link)}>
                          <Image
                              src={`${assetsBaseUrl}${event.imageSrc}`}
                              alt={event.title}
                              width={191}
                              height={155}
                              layout={'responsive'}
                          ></Image>
                          <Caption
                              variant="04"
                              design={{ marginTop: pxToRem(5) }}
                          >
                              {event.title}
                          </Caption>
                      </Flex>
                  ),
              }))
            : [],
        showSearchMenu,
        bottomNavigationAdditionalContent,
    };
}
